import type { Locale } from "./types";
import {
  getDynamicBooksStructuredMediaFormat
} from "../../server/structuredMediaRegistry.js";

export type ArticleRouteKey = "agentdriven-bokforing";

export type ArticleInternalLink = {
  label: string;
  href: `/#${string}`;
};

export type ArticleVisualItem = {
  title: string;
  body: string;
  assetId: string;
};

export type DescriptiveArticleSection = {
  id: string;
  heading: string;
  body: readonly string[];
  links?: readonly ArticleInternalLink[];
};

export type DescriptiveArticleContent = {
  key: ArticleRouteKey;
  slug: string;
  path: `/artiklar/${string}`;
  locale: Extract<Locale, "sv">;
  htmlLang: "sv";
  languageTag: "sv-SE";
  hostOwner: "dynamicbooks.se";
  canonicalUrl: `https://dynamicbooks.se${string}`;
  title: string;
  metaDescription: string;
  headline: string;
  lead: string;
  articleSection: string;
  breadcrumb: {
    homeLabel: string;
    categoryLabel: string;
    currentLabel: string;
  };
  datePublished: "2026-06-11";
  dateModified: "2026-06-11";
  keywords: readonly string[];
  imagePaths: readonly [`/assets/${string}`, ...`/assets/${string}`[]];
  primaryCta: {
    label: string;
    href: "/#estimate";
  };
  secondaryCta: {
    label: string;
    href: "/#login-handoff";
  };
  summaryTiles: readonly ArticleVisualItem[];
  workflow: readonly ArticleVisualItem[];
  capabilities: readonly (ArticleVisualItem & {
    detailLabel: string;
  })[];
  sections: readonly DescriptiveArticleSection[];
};

const agentdrivenBokforingImagePaths = [
  getDynamicBooksStructuredMediaFormat("article-agentdriven-bokforing-16x9", "png").publicPath,
  getDynamicBooksStructuredMediaFormat("article-agentdriven-bokforing-4x3", "png").publicPath,
  getDynamicBooksStructuredMediaFormat("article-agentdriven-bokforing-1x1", "png").publicPath
] as const satisfies readonly [`/assets/${string}`, ...`/assets/${string}`[]];

const agentdrivenBokforingArticle = {
  key: "agentdriven-bokforing",
  slug: "agentdriven-bokforing",
  path: "/artiklar/agentdriven-bokforing",
  locale: "sv",
  htmlLang: "sv",
  languageTag: "sv-SE",
  hostOwner: "dynamicbooks.se",
  canonicalUrl: "https://dynamicbooks.se/artiklar/agentdriven-bokforing",
  title: "Agentdriven bokföring i praktiken - DynamicBooks",
  metaDescription:
    "Följ hur DynamicBooks använder Data Rooms, BAS, moms, SIE, AP/AR, löneunderlag och Agent credits för att förbereda bokföring med kontroll.",
  headline: "Agentdriven bokföring i praktiken",
  lead:
    "DynamicBooks hjälper företag att samla underlag, förstå bokföringsfrågor och förbereda kontrollerade förslag innan omfattning, ansvar och nästa steg bekräftas.",
  articleSection: "Bokföring",
  breadcrumb: {
    homeLabel: "Hem",
    categoryLabel: "Artiklar",
    currentLabel: "Agentdriven bokföring i praktiken"
  },
  datePublished: "2026-06-11",
  dateModified: "2026-06-11",
  keywords: [
    "agentdriven bokföring",
    "bokföringsagent",
    "BAS kontoplan",
    "momsrapport",
    "SIE import",
    "AP/AR",
    "Data Rooms",
    "Agent credits"
  ],
  imagePaths: agentdrivenBokforingImagePaths,
  primaryCta: {
    label: "Begär en preliminär uppskattning",
    href: "/#estimate"
  },
  secondaryCta: {
    label: "Logga in i DynamicMail",
    href: "/#login-handoff"
  },
  summaryTiles: [
    {
      title: "Underlag samlas",
      body: "Bankfiler, kvitton, SIE-historik och rapportbehov förs in i en kontrollerad Data Room-logik.",
      assetId: "data-room-intake"
    },
    {
      title: "Agenten torrkör",
      body: "Förslag, avvikelser och bokföringsfrågor förbereds innan något går vidare.",
      assetId: "agent-dry-run"
    },
    {
      title: "Moms och rapporter",
      body: "Periodunderlag, PDF/XML/eSKD där konfigurerat och rapportartefakter blir granskningsbara.",
      assetId: "module-vat-reports"
    },
    {
      title: "Bekräftad väg",
      body: "Kund, konsult eller serviceteam bekräftar omfattning, ansvar och nästa steg.",
      assetId: "data-room-confirmed"
    }
  ],
  workflow: [
    {
      title: "Förbered",
      body: "Samla volym, källor, momsperiod, rapportbehov, AP/AR, SIE och lönepåverkande underlag.",
      assetId: "agent-prepare"
    },
    {
      title: "Torrkör",
      body: "Låt agentstödet skapa förslag, matchningskandidater, rapportpaket och tydliga undantag.",
      assetId: "agent-dry-run"
    },
    {
      title: "Kontrollera",
      body: "Granska saknade kvitton, momskoder, SIE-dubbletter, reskontrastatus och ansvarskänsliga frågor.",
      assetId: "agent-review"
    },
    {
      title: "Bekräfta",
      body: "Bestäm leveransväg, omfattning, Agent credits och handoff innan produktion eller servicearbete startar.",
      assetId: "agent-confirm"
    }
  ],
  capabilities: [
    {
      title: "BAS och kontoplan",
      body: "Kontonummer, kontonamn, kontokategorier, SIE-kontotyper och momskoder ger sammanhang till förslagen.",
      detailLabel: "BAS",
      assetId: "module-chart-balances"
    },
    {
      title: "Momsunderlag",
      body: "Momsperioder, valideringar och artefakter kan förberedas som kontrollunderlag där flödet är konfigurerat.",
      detailLabel: "Moms",
      assetId: "module-vat-reports"
    },
    {
      title: "SIE och rapporthistorik",
      body: "SIE-import/export, mappning, dubblettkontroll och rapportartefakter granskas steg för steg.",
      detailLabel: "SIE",
      assetId: "module-sie-migration"
    },
    {
      title: "AP/AR-reskontra",
      body: "Öppna poster, historik, betalstatus, kandidater och verifikationskopplingar blir synliga för kontroll.",
      detailLabel: "AP/AR",
      assetId: "module-ap-ar"
    },
    {
      title: "Lönepåverkande underlag",
      body: "Importer, batchkontroller, utlägg, journalförhandsvisning och lönerapportartefakter kan påverka perioden.",
      detailLabel: "Lön",
      assetId: "payroll-journals"
    },
    {
      title: "Data Rooms och Agent credits",
      body: "Data Rooms håller underlag och beslut samlade medan Agent credits beskriver förväntat körningsutrymme.",
      detailLabel: "Kapacitet",
      assetId: "seo-agent-credits"
    }
  ],
  sections: [
    {
      id: "kontrollerat-flode",
      heading: "När bokföringen behöver mer än en uppladdningsyta",
      body: [
        "Tänk ett svenskt konsult- eller e-handelsbolag som kommer in i månadsslutet med två bankkällor, kundfakturor, leverantörskvitton, en momsperiod, några lönepåverkande rader och en SIE-historik som behöver granskas.",
        "Agentdriven bokföring i DynamicBooks betyder att underlag, frågor och förslag förbereds i ett kontrollerat flöde. Agenten strukturerar arbetet, men bokföring, ansvarsnivå och nästa steg bekräftas innan arbetet går vidare."
      ],
      links: [
        {
          label: "Se hur bokföringsagenten presenteras på startsidan",
          href: "/#accounting-agent"
        }
      ]
    },
    {
      id: "data-room-fragor",
      heading: "Först: underlag och bokföringsfrågor samlas",
      body: [
        "Data Rooms samlar dokument, bankfiler, SIE-filer, momsperioder, rapportbehov, frågor och beslut så att saknade kvitton, osäker moms, dubbletter och öppna avvikelser kan granskas i rätt sammanhang.",
        "I stället för att frågor försvinner i e-post blir de synliga: vilket underlag saknas, vilka betalningar är omatchade, vilken SIE-fil är aktuell och vilka rapporter behövs för nästa beslut?"
      ],
      links: [
        {
          label: "Läs om Data Rooms på startsidan",
          href: "/#data-rooms"
        }
      ]
    },
    {
      id: "bas-kontomappning",
      heading: "BAS och kontomappning ger språket för förslagen",
      body: [
        "När kontoplanen finns på plats kan agentstödet läsa BAS-fält, kontokategorier, SIE-kontotyper och momskoder innan ett förslag tas fram. Det gör frågor lättare att knyta till rätt konto, period och kontrollpunkt.",
        "För kunden innebär det att en oklar bankrad eller ett kvitto kan diskuteras mot ett konkret konto och en konkret momslogik i stället för att bli en lös kommentar."
      ],
      links: [
        {
          label: "Se BAS- och momskorten",
          href: "/#seo-info"
        }
      ]
    },
    {
      id: "moms-underlag",
      heading: "Moms förbereds som underlag, inte som garanti",
      body: [
        "Momsflödet kan förbereda periodunderlag, valideringar och artefakter som PDF eller XML/eSKD där arbetsflödet är konfigurerat. Det är kontrollunderlag, inte ett löfte om autonom myndighetsinlämning eller garanterat resultat.",
        "Kvitto, ansvar och inlämningsväg hanteras enligt bekräftad leverans. Artikeln lovar därför inte att DynamicBooks själv lämnar in moms eller garanterar myndighetsutfall."
      ]
    },
    {
      id: "sie-rapporter",
      heading: "SIE, rapporter och historik granskas steg för steg",
      body: [
        "Vid migrering kan SIE-filer användas för import, export, mappning och dubblettkontroll. Rapportstödet kan ge balans, resultat, huvudbok, verifikationslista och rapportartefakter som granskningsbart underlag.",
        "Svåra historikfrågor, bokslutsfrågor eller korrigeringskedjor ska styras till kontroll innan omfattningen bekräftas."
      ],
      links: [
        {
          label: "Se rapportstödet på startsidan",
          href: "/#reports"
        }
      ]
    },
    {
      id: "ap-ar",
      heading: "AP/AR visar vad som är öppet, matchat och betalt",
      body: [
        "Kund- och leverantörsreskontra kan visa öppna poster, historik, betalstatus, kandidater och koppling till verifikationer.",
        "Det ger överblick inför bokföringen, men artikeln beskriver kontroll och spårbarhet, inte externa betalningsrälsar, automatisk indrivning eller betalningsinitiering."
      ]
    },
    {
      id: "lon-underlag",
      heading: "Lön kan påverka bokföringen, men ansvar och service sätts separat",
      body: [
        "Om löneunderlag påverkar perioden kan DynamicBooks beskriva import, beräkningar, batchkontroll, utlägg, journalförhandsvisning, utbetalningsutkast och lönerapportartefakter som underlag.",
        "Löneservice, arbetsgivaransvar, skattedeklaration och myndighetsinlämning fastställs separat. Publik artikeltext visar därför inga personidentifierare och lovar inga löneutbetalningar."
      ],
      links: [
        {
          label: "Läs om löneunderlag på startsidan",
          href: "/#payroll"
        }
      ]
    },
    {
      id: "serviceteam",
      heading: "När arbetet går till konsult- eller serviceteam",
      body: [
        "SIE-migrering, städning, bokslut, lönevolym, okända bankkällor och ansvarskänsliga frågor kan gå vidare till guidad uppstart.",
        "Då blir agentens arbete ett förberett underlag för kund, konsult eller serviceteam. Det ersätter inte professionell rådgivning och automatiserar inte ansvar eller regelefterlevnad."
      ],
      links: [
        {
          label: "Se tjänster och leveransvägar",
          href: "/#services"
        }
      ]
    },
    {
      id: "agent-credits",
      heading: "Agent credits gör kapaciteten tydligare",
      body: [
        "Agent credits beskriver förväntat körningsutrymme för agentarbete i Data Rooms. De hjälper kunden att förstå förberedelse, torrkörning, matchning, rapportarbete och granskningsloopar.",
        "Credits är en planeringssignal, inte pengar, inte slutliga Stripe-enheter och inte ett garanterat resultat. Fakturering och abonnemang hanteras efter inloggning och handoff."
      ],
      links: [
        {
          label: "Se programvara och priser",
          href: "/#software-pricing"
        }
      ]
    },
    {
      id: "nasta-steg",
      heading: "Nästa steg: preliminär uppskattning",
      body: [
        "Uppskattningen är preliminär tills källor, underlag, ansvarsnivå, omfattning och startdatum har granskats.",
        "Befintliga kunder fortsätter via DynamicMail när konto, Data Rooms och leveransväg är klara."
      ],
      links: [
        {
          label: "Begär en preliminär uppskattning",
          href: "/#estimate"
        },
        {
          label: "Logga in i DynamicMail",
          href: "/#login-handoff"
        }
      ]
    }
  ]
} as const satisfies DescriptiveArticleContent;

export const descriptiveArticleRegistry = {
  "agentdriven-bokforing": agentdrivenBokforingArticle
} satisfies Record<ArticleRouteKey, DescriptiveArticleContent>;

export const descriptiveArticles = Object.values(descriptiveArticleRegistry);

export const descriptiveArticlesByLocale = {
  sv: descriptiveArticles,
  en: []
} satisfies Record<Locale, readonly DescriptiveArticleContent[]>;

export function findDescriptiveArticleByPath(pathname: string): DescriptiveArticleContent | null {
  const normalizedPath = normalizeArticlePathname(pathname);

  return descriptiveArticles.find((article) => article.path === normalizedPath) ?? null;
}

export function isDescriptiveArticlePath(pathname: string): boolean {
  return Boolean(findDescriptiveArticleByPath(pathname));
}

function normalizeArticlePathname(pathname: string): string {
  const normalized = pathname === "" ? "/" : pathname;

  return normalized.length > 1 ? normalized.replace(/\/$/, "") : normalized;
}
